<template>
  <div class="admin-dashboard fade-in">
    <!-- ===== Global Loader ===== -->
     <transition name="fade-slide">
      <div v-if="loading" class="global-loader">
        <div class="loader-spinner"></div>
        <p>{{ loadingText }}</p>
      </div>
    </transition>

    <!-- ===== Header ===== -->
    <div class="header-row">
      <h1 class="page-title">
        <i class="bx bx-grid-alt"></i> Super Admin Dashboard
      </h1>
      <div class="filters">
        <select v-model="selectedRange" class="select-theme" @change="loadDashboard">
          <option value="today">Today</option>
          <option value="last_7_days">Last 7 Days</option>
          <option value="last_30_days">Last 30 Days</option>
          <option value="last_12_months">Last 12 Months</option>
        </select>
      </div>
    </div>

    <!-- ===== KPI Section ===== -->
    <div class="kpi-row fade-in">
      <div class="kpi-card" v-for="(kpi, i) in kpis" :key="i">
        <div>
          <h4>{{ kpi.label }}</h4>
          <div class="value">
            <span v-if="!loading">{{ kpi.value || '—' }}</span>
            <span v-else class="skeleton w-20 h-4"></span>
          </div>
        </div>
        <i :class="kpi.icon + ' kpi-icon'"></i>
      </div>
    </div>

    <!-- ===== Charts Row ===== -->
    <div class="charts-grid fade-in">
      <!-- Left Chart -->
      <div class="chart-box large">
        <h4>Revenue from Memberships (Daily)</h4>
        <div v-if="!loading && chartData.length" id="revenueChart" class="chart"></div>
        <div v-else-if="loading" class="skeleton w-full h-60 rounded"></div>
        <div v-else class="empty-state">
          <i class="bx bx-bar-chart-alt"></i>
          <p>No revenue data found</p>
        </div>
      </div>

      <!-- Right Chart -->
      <!-- <div class="chart-box small">
        <h4>Platform Distribution</h4>
        <div v-if="!loading && platformData.length" id="platformChart" class="chart"></div>
        <div v-else-if="loading" class="skeleton w-full h-60 rounded"></div>
        <div v-else class="empty-state">
          <i class="bx bx-pie-chart-alt-2"></i>
          <p>No platform data available</p>
        </div>
      </div> -->
    </div>

    <!-- ===== Revenue Breakdown Table ===== -->
    <div class="table-box fade-in">
      <div class="table-header">
        <h4>Revenue Breakdown by Plan</h4>
      </div>

      <table v-if="!loading && plans.length">
        <thead>
          <tr>
            <th>Plan Name</th>
            <th>Subscribers</th>
            <th>Revenue ($)</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(plan, i) in plans" :key="i">
            <td>{{ plan.plan_title }}</td>
            <td>{{ plan.no_of_subscribers }}</td>
            <td>{{ plan.revenue.toLocaleString() }}</td>
          </tr>
        </tbody>
      </table>

      <div v-else-if="loading" class="skeleton w-full h-24 rounded"></div>
      <div v-else class="empty-state">
        <i class="bx bx-wallet-alt"></i>
        <p>No plan data available</p>
      </div>
    </div>

    <!-- ===== 2-Column Detail Section ===== -->
    <div class="detail-grid fade-in">
      <!-- Latest Users -->
      <div class="card">
        <h4>Paid Users</h4>
        <ul v-if="!loading && latestUsers.length" class="list">
          <li v-for="(user, i) in latestUsers" :key="i">
            <div class="info">
              <strong>{{ user.fname }} {{ user.lname }}</strong>
              <small>{{ user.email }}</small>
            </div>
            <span class="badge">{{ user.user_plan_name || 'Free' }}</span>
          </li>
        </ul>
        <div v-else-if="loading" class="skeleton w-full h-24 rounded"></div>
        <div v-else class="empty-state">
          <i class="bx bx-user"></i>
          <p>No users found</p>
        </div>
      </div>

      <!-- Top Websites -->
      <div class="card">
        <h4>Free Users</h4>
        <ul v-if="!loading && freeUsers.length" class="list">
          <li v-for="(user, i) in freeUsers" :key="i">
            <div class="info">
              <strong>{{ user.fname }} {{ user.lname }}</strong>
              <small>{{ user.email }}</small>
            </div>
            <span class="badge">{{ user.user_plan_name || 'Free' }}</span>
          </li>
        </ul>
        <div v-else-if="loading" class="skeleton w-full h-24 rounded"></div>
        <div v-else class="empty-state">
          <i class="bx bx-globe"></i>
          <p>No website data available</p>
        </div>
      </div>
    </div>

    <br><br><br><br><br>
  </div>
</template>

<script>
import ApexCharts from "apexcharts";
import { getAdminDashboardReport } from "@/apis/aupadminapis"; // ✅ API import

export default {
  name: "SuperAdminDashboard",
  data() {
    return {
      loading: false,
      loadingText: "Loading dashboard data...",
      selectedRange: "today",

      // UI-bound data
      kpis: [],
      plans: [],
      latestUsers: [],
      freeUsers:[],
      topWebsites: [],
      chartData: [],
      platformData: [],

      // Chart instance references
      chartRevenue: null,
      chartPlatform: null,
    };
  },

  async mounted() {
    await this.loadDashboard();
  },

  methods: {
    /** 🔹 Load Dashboard Data */
    async loadDashboard() {
      try {
        this.loading = true;
        this.loadingText = "Fetching admin dashboard report...";

        const res = await getAdminDashboardReport();

        if (res?.status) {
          // 🧠 Set KPI Metrics
          this.kpis = [
            { label: "Total Users", value: res.total_users || 0, icon: "bx bx-user" },
            { label: "Paid Users", value: res.paid_users || 0, icon: "bx bx-user-check" },
            { label: "Websites", value: res.total_websites || 0, icon: "bx bx-globe" },
            {
              label: "Revenue",
              value: `$${(res.revenue || 0).toLocaleString()}`,
              icon: "bx bx-line-chart",
            },
            {
              label: "Active Subscriptions",
              value: res.active_subscriptions || 0,
              icon: "bx bx-credit-card",
            },
            {
              label: "Support Tickets",
              value: res.support_tickets || 0,
              icon: "bx bx-support",
            },
          ];

          
          // 💰 Plans Table
          this.plans = res.plans || [];

          // 👥 Latest Users
          this.latestUsers = res.latest_users || [];

          // 🌐 Top Websites
          this.freeUsers = res.free_users || [];

          // 📈 Chart Data
          this.chartData = (res.last7days_revenue || []).map((d) => ({
            x: d.day,
            y: d.amount,
          }));

          // 🧭 Platform Chart Data
          const catCount = res.category_count || {};
          this.platformData = [
            catCount.users || 0,
            catCount.websites || 0,
            catCount.bookings || 0,
            catCount.invoices || 0,
          ];

          // 🔄 Render Charts
          this.$nextTick(() => {
            this.renderRevenueChart();
            this.renderPlatformChart();
          });
        } else {
          console.warn("Dashboard API returned no data:", res);
        }
      } catch (err) {
        console.error("Dashboard Load Error:", err);
      } finally {
        this.loading = false;
      }
    },

    /** 📊 Render Revenue (Area) Chart */
    renderRevenueChart() {
      const el = document.querySelector("#revenueChart");
      if (!el) return;

      if (this.chartRevenue) {
        this.chartRevenue.destroy();
      }

      this.chartRevenue = new ApexCharts(el, {
        chart: {
          type: "area",
          height: 300,
          toolbar: { show: false },
          animations: { enabled: true, easing: "easeinout", speed: 800 },
        },
        series: [
          {
            name: "Revenue ($)",
            data: this.chartData.map((d) => d.y),
          },
        ],
        xaxis: {
          categories: this.chartData.map((d) => d.x),
          labels: { style: { fontSize: "12px" } },
        },
        stroke: { curve: "smooth", width: 3 },
        colors: ["#111827"],
        fill: {
          type: "gradient",
          gradient: {
            shadeIntensity: 0.8,
            opacityFrom: 0.7,
            opacityTo: 0.3,
          },
        },
        grid: { borderColor: "#E5E7EB" },
      });

      this.chartRevenue.render();
    },

    /** 🥧 Render Platform (Donut) Chart */
    renderPlatformChart() {
      const el = document.querySelector("#platformChart");
      if (!el) return;

      if (this.chartPlatform) {
        this.chartPlatform.destroy();
      }

      this.chartPlatform = new ApexCharts(el, {
        chart: { type: "donut", height: 300 },
        labels: ["Users", "Websites", "Bookings", "Invoices"],
        series: this.platformData,
        colors: ["#111827", "#1f2937", "#4b5563", "#9ca3af"],
        legend: { position: "bottom" },
        dataLabels: { enabled: false },
      });

      this.chartPlatform.render();
    },
  },
};
</script>


<style scoped>
.admin-dashboard {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* Animations */
.fade-in {
  animation: fadeInUp 0.6s ease forwards;
  opacity: 0;
  transform: translateY(10px);
}
@keyframes fadeInUp {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* KPI Cards */
.kpi-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
}
.kpi-card {
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: var(--transition);
}
.kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.08);
}
.kpi-card h4 {
  font-size: 13px;
  color: var(--muted);
}
.kpi-card .value {
  font-weight: 700;
  font-size: 20px;
}
.kpi-icon {
  font-size: 20px;
  color: var(--primary);
  opacity: 0.8;
}

.chart-box {
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 16px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
}
.chart {
  height: 300px;
}

/* Revenue Table */
.table-box {
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 16px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
}
table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
th, td {
  padding: 10px;
  border-bottom: 1px solid var(--border);
}
th {
  color: var(--muted);
  font-weight: 500;
}
tr:hover {
  background: #f9fafb;
}

/* Detail Grid */
.detail-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}
.card {
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 16px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
}
.list {
  list-style: none;
  padding: 0;
  margin: 0;
}
.list li {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px dashed var(--border);
  padding: 8px 0;
}
.info {
  display: flex;
  flex-direction: column;
}
.badge {
  background: #e0e7ff;
  color: var(--primary);
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 6px;
  font-weight: 600;
}
.badge.success {
  background: #dcfce7;
  color: #166534;
}

/* System Health */
.system-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
}
.system-card {
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  transition: var(--transition);
}
.system-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.07);
}
.system-card i {
  font-size: 22px;
  color: var(--primary);
}
.system-card h4 {
  font-size: 13px;
  color: var(--muted);
}
.system-card p {
  font-weight: 600;
}


@media (max-width: 768px) {
  .kpi-row {
    grid-template-columns: 1fr 1fr;
  }
}

/* ============================================
   🧠 ADDITIONAL POLISH FOR SUPER ADMIN DASHBOARD
   ============================================ */

/* ===== Header ===== */
.header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 10px;
}

.page-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 1.3rem;
  font-weight: 700;
  color: var(--text);
}

.page-title i {
  color: var(--primary);
  font-size: 1.5rem;
}

.filters {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.select-theme {
  background: #fff;
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 13px;
  color: var(--text);
  cursor: pointer;
  transition: var(--transition);
}

.select-theme:hover {
  border-color: var(--primary);
}

/* ===== Chart Animation Fix ===== */
.apexcharts-canvas {
  width: 100% !important;
  height: auto !important;
}

.apexcharts-tooltip {
  background: #111827 !important;
  color: #fff !important;
  border-radius: 6px !important;
  border: none !important;
}

/* ===== Table Hover + Transitions ===== */
.table-box table tr {
  transition: background 0.25s ease;
}

.table-box table tr:hover {
  background: #f3f4f6;
  transform: scale(1.001);
}

/* ===== Fade + Motion Utilities ===== */
.fade-up {
  opacity: 0;
  transform: translateY(12px);
  animation: fadeUp 0.6s ease forwards;
}

@keyframes fadeUp {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ===== Shadow & Hover Lift ===== */
.hover-lift {
  transition: all 0.25s ease;
}

.hover-lift:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.08);
}

/* ===== Scrollbar Customization ===== */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}
::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 8px;
}
::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}
::-webkit-scrollbar-track {
  background: transparent;
}

/* ===== Typography Fixes ===== */
h4 {
  font-weight: 600;
  color: var(--text);
}

small {
  font-size: 12px;
  color: var(--muted);
}

/* ===== Badge States ===== */
.badge.warning {
  background: #fef9c3;
  color: #92400e;
}

.badge.danger {
  background: #fee2e2;
  color: #991b1b;
}

/* ===== Responsive Optimizations ===== */
@media (max-width: 1024px) {
  .header-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .page-title {
    font-size: 1.15rem;
  }


  .chart-box.large,
  .chart-box.small {
    width: 100% !important;
  }

  .table-box {
    overflow-x: auto;
  }
}

@media (max-width: 768px) {
  .kpi-row {
    grid-template-columns: 1fr 1fr !important;
  }

  .detail-grid {
    grid-template-columns: 1fr !important;
  }

  .filters select {
    width: 100% !important;
  }

  .list li {
    flex-direction: column;
    align-items: flex-start;
  }

  .page-title {
    justify-content: center;
    text-align: center;
    width: 100%;
  }

  .filters {
    justify-content: center;
    width: 100%;
  }

  .select-theme {
    width: 100%;
  }

  .chart {
    height: 250px !important;
  }

  .kpi-card {
    padding: 14px;
  }

  .system-card {
    padding: 14px;
  }
}

@media (max-width: 480px) {
  .page-title {
    font-size: 1rem;
  }

  .kpi-card .value {
    font-size: 18px;
  }

  th,
  td {
    padding: 8px;
  }

  .badge {
    font-size: 11px;
  }

  .chart {
    height: 220px !important;
  }
}

/* ===== Subtle Microinteractions ===== */
button,
.select-theme {
  transition: all 0.25s ease-in-out;
}

button:hover,
.select-theme:focus {
  box-shadow: 0 0 0 3px rgba(17, 24, 39, 0.15);
  border-color: var(--primary);
}

/* ===== Card Subtle Glow for Dashboard Hero ===== */
.chart-box.large:hover,
.table-box:hover,
.card:hover {
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}

/* ===== Empty State (if no data) ===== */
.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  color: var(--muted);
  font-size: 14px;
  padding: 40px 0;
}

.empty-state i {
  font-size: 36px;
  margin-bottom: 6px;
  opacity: 0.6;
}

/* ===== Loader (for future API integration) ===== */
.loader {
  width: 36px;
  height: 36px;
  border: 3px solid #e5e7eb;
  border-top-color: var(--primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
/* ===== FIX: Reduce Excess Space Between Dashboard Rows ===== */
.admin-dashboard {
  display: flex;
  flex-direction: column;
  gap: 16px; /* reduced from 24px for tighter vertical spacing */
}

/* Remove unintended extra space between consecutive sections */
.kpi-row,
.system-row,
.charts-grid,
.table-box,
.detail-grid {
  margin-bottom: 0 !important;
}

/* Add controlled spacing after major blocks only */
.kpi-row + .system-row,
.system-row + .charts-grid,
.charts-grid + .table-box,
.table-box + .detail-grid {
  margin-top: 8px !important; /* subtle rhythm */
}

/* Optional: visually group metrics + charts */
.system-row {
  margin-top: 4px;
  margin-bottom: 4px;
}

/* Slightly tighten chart and table padding */
.chart-box,
.table-box,
.card {
  padding: 14px !important;
}
/* ==================================================
   🦴 SKELETON LOADER EFFECTS (for loading states)
   ================================================== */
.skeleton {
  position: relative;
  overflow: hidden;
  background: #f3f4f6;
  border-radius: 6px;
}
.skeleton::after {
  content: "";
  position: absolute;
  top: 0;
  left: -150px;
  height: 100%;
  width: 150px;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.6) 50%,
    rgba(255, 255, 255, 0) 100%
  );
  animation: shimmer 1.6s infinite;
}
@keyframes shimmer {
  0% {
    left: -150px;
  }
  100% {
    left: 100%;
  }
}

/* Use skeleton for charts, KPI, and table placeholders */
.skeleton-box {
  height: 160px;
  border-radius: 10px;
  margin-bottom: 12px;
  background: #f3f4f6;
}
.skeleton-line {
  height: 14px;
  border-radius: 4px;
  background: #e5e7eb;
  margin-bottom: 8px;
}
.skeleton-line.short {
  width: 50%;
}
.skeleton-line.medium {
  width: 75%;
}
.skeleton-line.long {
  width: 100%;
}

/* ==================================================
   🧭 EMPTY STATE PLACEHOLDERS (for empty data)
   ================================================== */
.empty-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 40px 0;
  color: var(--muted);
  background: var(--card);
  border: 1px dashed var(--border);
  border-radius: 10px;
  gap: 8px;
}
.empty-block i {
  font-size: 32px;
  color: #9ca3af;
}
.empty-block p {
  font-size: 14px;
  margin: 0;
  color: #6b7280;
}

/* ==================================================
   ✨ MICROINTERACTIONS + ANIMATION POLISH
   ================================================== */
.fade-smooth {
  animation: fadeSmooth 0.4s ease-in-out;
}
@keyframes fadeSmooth {
  0% {
    opacity: 0;
    transform: translateY(8px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

/* While loading, reduce opacity and disable pointer events */
.loading-blur {
  opacity: 0.5;
  pointer-events: none;
  transition: opacity 0.3s ease;
}

/* ==================================================
   📊 CHART PLACEHOLDER IMPROVEMENTS
   ================================================== */
.chart.skeleton {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  color: #9ca3af;
  background: repeating-linear-gradient(
    45deg,
    #f3f4f6,
    #f3f4f6 10px,
    #e5e7eb 10px,
    #e5e7eb 20px
  );
  animation: subtleFade 1.2s ease-in-out infinite alternate;
}

@keyframes subtleFade {
  0% {
    opacity: 0.85;
  }
  100% {
    opacity: 1;
  }
}

/* ==================================================
   📦 TABLE ROW PLACEHOLDER EFFECT
   ================================================== */
.table-box.loading tbody tr {
  pointer-events: none;
  opacity: 0.7;
}
.table-box.loading tbody tr td {
  position: relative;
  overflow: hidden;
}
.table-box.loading tbody tr td::after {
  content: "";
  position: absolute;
  top: 0;
  left: -120px;
  width: 120px;
  height: 100%;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0),
    rgba(255, 255, 255, 0.5),
    rgba(255, 255, 255, 0)
  );
  animation: shimmer 1.8s infinite;
}
</style>
